import { useEffect, useMemo, useRef, useState } from "react";
import {
    Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
    Input, InputGroup, InputGroupText, Label
} from "reactstrap";
import { Search, X } from "react-bootstrap-icons";
import "../Shared/TenantSelector.css";
import "./TenantSearchDropdown.css";
import SwitchHQWarningModal from "../SwitchHQWarningModal/SwitchHQWarningModal";
import { changeOrganizationAndLoad, setSelectedTenant, useStore } from "../../utilities/appStore";

export default function TenantSearchDropdown({
    dataMap = {},
    onSelect = () => { },
    placeholder = "Search…",
    maxResults = 8
}) {
    const orgId = useStore((state) => state.CurrentOrganizationId);
    const [modalOpen, setModalOpen] = useState(false);
    const [pendingSelection, setPendingSelection] = useState(null);

    // Get the selected tenant entry from dataMap
    const getSelectedEntry = () => {
        return Object.values(dataMap).find((t) => t.selected);
    };

    const formatTenantLabel = (entry) => {
        return entry
            ? `${entry.name} - ${entry.accountId}`
            : "Select HQ";
    };

    const formatSelectedTenantLabel = () => {
        const selectedEntry = getSelectedEntry();
        return formatTenantLabel(selectedEntry);
    };

    const setInputTextAsTheCurrentTenant = () => {
        setInputValue(formatSelectedTenantLabel());
    }
    
    const toggleModal = () => {
        setInputTextAsTheCurrentTenant();
        setModalOpen((prev) => !prev);
    }
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(() => {
        return formatSelectedTenantLabel();
    });

    const [query, setQuery] = useState("");           // what we filter on
    const [activeIndex, setActiveIndex] = useState(0);
    const rootRef = useRef(null);
    const inputRef = useRef(null);

    // Update inputValue when the selected tenant's name changes in dataMap
    useEffect(() => {
        const selectedEntry = getSelectedEntry();
        if (selectedEntry) {
            const newLabel = formatTenantLabel(selectedEntry);
            setInputValue(newLabel);
        }
    }, [dataMap]);
    

    // flatten {index: {accountId, name}}
    const items = useMemo(
        () =>
            Object.entries(dataMap).map(([index, v]) => ({
                index,
                accountId: v?.accountId ?? "",
                name: v?.name ?? ""
            })),
        [dataMap]
    );

    // filter by name or accountId
    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items.slice(0, maxResults);
        return items
            .filter(
                (x) =>
                    String(x.accountId).toLowerCase().includes(q) ||
                    String(x.name).toLowerCase().includes(q)
            )
            .slice(0, maxResults);
    }, [items, query, maxResults]);

    // recompute 'open' based on focus + results
    const openIfNeeded = () => {
        const focused = document.activeElement === inputRef.current;
        setOpen(focused && results.length > 0);
    };

    // keep highlight sane on results change
    useEffect(() => {
        setActiveIndex(0);
    }, [results.length]);

    // close on outside click
    useEffect(() => {
        const onDocDown = (e) => {
            if (!rootRef.current?.contains(e.target)) {
                setOpen(false);
                setInputTextAsTheCurrentTenant();
            }
        };
        document.addEventListener("mousedown", onDocDown);
        return () => document.removeEventListener("mousedown", onDocDown);
    }, [dataMap]);

    const handleSelect = (item) => {
        if (item.accountId !== orgId) {
            setPendingSelection(item);
            setModalOpen(true);
        } else {
            setInputTextAsTheCurrentTenant();
        }
        
        //onSelect(item);
        setQuery("");        // <-- important (prevents "No matches" after select)
        setOpen(false);      // close the menu
    };
    const confirmSelectionChange = () => {
        if (pendingSelection) {
            // show friendly label in the input, but clear query so we don't re-filter to "no matches"
            const label = `${pendingSelection.name} (${pendingSelection.accountId})`;
            setInputValue(label);
            setSelectedTenant(pendingSelection.accountId);
            changeOrganizationAndLoad(pendingSelection.accountId);
        }
        setModalOpen(false);
    };

    const onKeyDown = (e) => {
        if (!results.length) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            handleSelect(results[activeIndex]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    // Only open when there are results
    const isOpen = open && results.length > 0;

    return (
        <>
            <div ref={rootRef} className="searchbox-wrap d-flex align-items-center gap-2">
                <Label className="mb-0 fw-normal text-white" style={{ minWidth: "110px" }}>
                    HQ Selection:
                </Label>

                {/* IMPORTANT: keep toggle a no-op so Reactstrap doesn't fight our state */}
                <Dropdown isOpen={isOpen} toggle={() => { }} direction="down">
                    <DropdownToggle
                        caret={false}
                        tag="div"
                        className="w-100"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => inputRef.current?.focus()}
                        style={{
                            minWidth: "300px",
                            textAlign: "left",
                            border: "none",
                            boxShadow: "none",
                        }}
                    >
                        <InputGroup className="searchbox">
                            <InputGroupText className="searchbox-icon-left">
                                <Search color='gray' />
                            </InputGroupText>
                            <Input
                                innerRef={inputRef}
                                value={inputValue}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setInputValue(v); // what user sees
                                    setQuery(v);      // what we filter on
                                    setOpen(true);    // show menu while typing
                                }}
                                onFocus={openIfNeeded}
                                onKeyDown={onKeyDown}
                                placeholder={placeholder}
                                autoComplete="off"
                                className="searchbox-input"
                            />
                            {inputValue && (
                                <InputGroupText
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setInputValue("");
                                        setQuery("");
                                        inputRef.current?.focus();
                                    }}
                                    className="searchbox-icon-right"
                                >
                                    <X color='gray' />
                                </InputGroupText>
                            )}
                        </InputGroup>
                    </DropdownToggle>

                    <DropdownMenu className="searchbox-menu" style={{ width: "100%", borderRadius: 0 }}>
                        {/* Only show 'No matches' when the user is actively typing a non-empty query */}
                        {query.trim() && results.length === 0 ? (
                            <DropdownItem disabled>No matches</DropdownItem>
                        ) : (
                            results.map((item, idx) => (
                                <DropdownItem
                                    key={`${item.index}-${item.accountId}`}
                                    active={idx === activeIndex}
                                    onMouseEnter={() => setActiveIndex(idx)}
                                    onClick={() => handleSelect(item)}
                                >
                                    <div className="d-flex justify-content-between gap-2">
                                        <span>{`${item.name} (${item.accountId})`}</span>
                                    </div>
                                </DropdownItem>
                            ))
                        )}
                    </DropdownMenu>
                </Dropdown>
            </div>
            <SwitchHQWarningModal
                modalOpen={modalOpen}
                confirmSelectionChange={confirmSelectionChange}
                toggleModal={toggleModal}
            />
        </>
    );
}
